import { useMsal } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { QueryCacheKey } from "../../api/queryClient";
import { config } from "../../config";
import { isAxiosError, publicClientAppScopes, useApiContext } from "../../utils";
import { useLoginWithRedirect } from "../../utils/useLoginWithRedirect";
import { OidcLogin } from "../OidcLogin";

import { useDashboard, useDashboardPermissions } from "./webDashboard";
import { WebDashboardViewer } from "./WebDashboardViewer";

// How long to poll for the Google Identity Services script before giving up and
// showing the plain fallback login button. GIS can be blocked entirely by corporate
// proxies / ad blockers, so the sign-in page must not depend on it (ENG-8230:
// viewers saw a buttonless "Sign In Required" card).
const GIS_LOAD_TIMEOUT_MS = 5000;

// Mirrors the GIS button label CSS (.nsm7Bb-HzV7m-LgbsSe): Google Sans 500, 14px,
// .25px tracking, #3c4043. Google Sans is proprietary; Google Sans Flex (loaded in
// index.html) is its variable successor and visually equivalent at weight 450.
const GIS_MATCHING_LABEL_STYLE: React.CSSProperties = {
  fontFamily: "'Google Sans Flex', 'Google Sans', Roboto, arial, sans-serif",
  fontSize: 14,
  fontWeight: 450,
  letterSpacing: "0.25px",
  color: "#3c4043",
};

const GIS_MATCHING_BUTTON_STYLE: React.CSSProperties = {
  ...GIS_MATCHING_LABEL_STYLE,
  display: "flex",
  alignItems: "center",
  width: 215,
  height: 40,
  padding: "0 12px",
  borderRadius: 4,
  border: "1px solid #dadce0",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const CARD_STYLE: React.CSSProperties = {
  backgroundColor: "#fff",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  borderRadius: 12,
  padding: 32,
  maxWidth: 448,
  width: "100%",
  textAlign: "center",
};

const CARD_ICON_WRAPPER_STYLE: React.CSSProperties = {
  width: 64,
  height: 64,
  margin: "0 auto 16px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const CARD_TITLE_STYLE: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  color: "#111827",
  margin: "0 0 12px",
};

const CARD_BODY_STYLE: React.CSSProperties = {
  color: "#4b5563",
  margin: "0 0 24px",
};

const SECONDARY_BUTTON_STYLE: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: 14,
  color: "#374151",
  backgroundColor: "#f3f4f6",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

// Poll until `isReady` returns true, then invoke onReady. Returns a cancel function;
// the loop stops on cancel or after timeoutMs (invoking onTimeout).
const pollUntilReady = (
  isReady: () => boolean,
  onReady: () => void,
  onTimeout?: () => void,
  timeoutMs: number = GIS_LOAD_TIMEOUT_MS
): (() => void) => {
  let cancelled = false;
  let timer: number | undefined;
  const startedAt = Date.now();
  const poll = () => {
    if (cancelled) {
      return;
    }
    if (isReady()) {
      onReady();
      return;
    }
    if (Date.now() - startedAt >= timeoutMs) {
      onTimeout?.();
      return;
    }
    timer = window.setTimeout(poll, 100);
  };
  poll();
  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
};

const isGisAvailable = (): boolean => typeof google !== "undefined" && Boolean(google.accounts);

const MicrosoftLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" style={{ marginRight: 8 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const CardIcon: React.FC<{ background: string; children: React.ReactNode }> = ({
  background,
  children,
}) => <div style={{ ...CARD_ICON_WRAPPER_STYLE, backgroundColor: background }}>{children}</div>;

/**
 * Route-level gate for /dashboards/:dashboardId. Renders the access-denied /
 * error experience in the parent DOM — before WebDashboardViewer (and its heavy
 * srcDoc iframe) mounts — and only mounts the viewer when the dashboard fetch
 * isn't in an error state. The viewer shares the same react-query keys, so no
 * extra requests are made.
 */
export const WebDashboardAccessGate: React.FC = () => {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const location = useLocation();
  const requestSuperAdminView = new URLSearchParams(location.search).has("view_as_super_admin");

  const { error: dashboardError, isFetching: isDashboardFetching } = useDashboard(
    dashboardId,
    null,
    requestSuperAdminView
  );
  const { data: permissions, isLoading: isLoadingPermissions } = useDashboardPermissions(
    dashboardId,
    requestSuperAdminView
  );

  const isAuthenticated = permissions?.is_authenticated || false;
  const userEmail = permissions?.user_email || null;

  const isAccessDenied = isAxiosError(dashboardError) && dashboardError.response?.status === 403;
  const accessDeniedReason = isAccessDenied
    ? (dashboardError.response?.data as { reason?: string } | undefined)?.reason
    : undefined;
  const isAccessDeniedBuildInProgress = accessDeniedReason === "build_in_progress";
  const isAccessDeniedBuildFailed = accessDeniedReason === "build_failed";
  const isNotFound = isAxiosError(dashboardError) && dashboardError.response?.status === 404;

  const shouldShowSignIn =
    isAccessDenied &&
    !isLoadingPermissions &&
    !isAuthenticated &&
    !isAccessDeniedBuildInProgress &&
    !isAccessDeniedBuildFailed;

  // A viewer whose org enforces SSO can sign in with Google/Microsoft (session
  // cookie is set) but holds no IdP token, so every authenticated fetch 401s with
  // SSO_AUTH_FAILED and the OAuth buttons below can never break the cycle
  // (ENG-8359). getUserSSOInfo skips the SSO check server-side, so it separates
  // that viewer (200 with sso_enabled, not sso-authenticated) from a signed-out
  // one (401) — only the latter should see the OAuth buttons.
  const { apiClient } = useApiContext();
  const {
    data: userSSOInfoResponse,
    isLoading: isLoadingUserSSOInfo,
    isFetching: isFetchingUserSSOInfo,
  } = useQuery([QueryCacheKey.USER_SSO_INFO], () => apiClient.getUserSSOInfo(), {
    enabled: shouldShowSignIn,
    retry: false,
  });

  const userSSOInfo = userSSOInfoResponse?.data;
  const ssoIdentityProvider =
    shouldShowSignIn && userSSOInfo?.sso_enabled && !userSSOInfo.is_sso_authenticated
      ? userSSOInfo.id_provider
      : undefined;

  // Resolve the SSO question before rendering the OAuth buttons so an SSO-bound
  // viewer never sees (and re-clicks) a sign-in path that can't help them.
  const shouldShowOAuthButtons =
    shouldShowSignIn && !isLoadingUserSSOInfo && !isFetchingUserSSOInfo && !ssoIdentityProvider;

  useEffect(() => {
    if (ssoIdentityProvider) {
      Sentry.addBreadcrumb({
        category: "web_dashboard",
        message: "SSO sign-in step shown to dashboard viewer",
        level: "info",
      });
    }
  }, [ssoIdentityProvider]);

  const googleSignInButtonRef = useRef<HTMLDivElement>(null);
  const [showGoogleSignInFallback, setShowGoogleSignInFallback] = useState(false);

  useEffect(() => {
    if (!shouldShowOAuthButtons) {
      return;
    }

    // A previous poll may have timed out transiently; give GIS another chance before
    // settling on the fallback button again.
    setShowGoogleSignInFallback(false);

    Sentry.addBreadcrumb({
      category: "web_dashboard",
      message: "Sign-in page shown to unauthenticated dashboard viewer",
      level: "info",
    });

    return pollUntilReady(
      () => isGisAvailable() && Boolean(googleSignInButtonRef.current),
      () => {
        const node = googleSignInButtonRef.current;
        if (!node) {
          return;
        }
        const state = encodeURIComponent(JSON.stringify({ redirect: window.location.href }));
        google.accounts.id.initialize({
          client_id: config.GOOGLE_CLIENT_ID,
          ux_mode: "redirect",
          login_uri: `${config.BASE_URL}/api/auth/google_jwt_callback`,
          // `state` is round-tripped by the backend callback; not in IdConfiguration
          // typings (same pattern as Login.tsx).
          ...{ state },
        });
        node.innerHTML = "";
        google.accounts.id.renderButton(node, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          // Match the custom Microsoft button below (215x40).
          width: "215",
        });
        Sentry.addBreadcrumb({
          category: "web_dashboard",
          message: "GIS sign-in button rendered on dashboard access page",
          level: "info",
        });
      },
      () => {
        // GIS never became available (blocked script, network issue) — swap in the
        // plain login button so the viewer always has a way in.
        setShowGoogleSignInFallback(true);
        Sentry.captureMessage(
          "GIS unavailable on dashboard sign-in page; showing fallback login button",
          { level: "warning", extra: { dashboardId } }
        );
      }
    );
  }, [shouldShowOAuthButtons, dashboardId]);

  const loginWithRedirect = useLoginWithRedirect();
  const handleFallbackGoogleLogin = () => {
    // Keep hash too, matching the full-href redirect used by the GIS path.
    loginWithRedirect({
      url: window.location.pathname + window.location.search + window.location.hash,
    });
  };

  const { instance: msalInstance } = useMsal();
  const queryClient = useQueryClient();
  const [isMicrosoftSignInInFlight, setIsMicrosoftSignInInFlight] = useState(false);
  const handleMicrosoftSignIn = useCallback(async () => {
    try {
      setIsMicrosoftSignInInFlight(true);
      const res = await msalInstance.loginPopup({ scopes: publicClientAppScopes });
      if (res?.account) {
        msalInstance.setActiveAccount(res.account);
      }
      if (dashboardId) {
        queryClient.invalidateQueries([
          QueryCacheKey.WEB_DASHBOARD_PERMISSIONS,
          dashboardId,
          requestSuperAdminView,
        ]);
        queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD, dashboardId]);
        queryClient.invalidateQueries([QueryCacheKey.CLIENT_FLAGS]);
        queryClient.invalidateQueries([QueryCacheKey.USER_SSO_INFO]);
      }
    } catch (error) {
      console.error("Microsoft sign-in failed:", error);
    } finally {
      setIsMicrosoftSignInInFlight(false);
    }
  }, [dashboardId, msalInstance, queryClient, requestSuperAdminView]);

  const handleSignOut = async () => {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      // Ensure MSAL session doesn't silently re-auth on reload.
      await msalInstance.clearCache();
      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Failed to sign out");
      }
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (!dashboardError) {
    return <WebDashboardViewer />;
  }

  // The dashboard fetch failed but permissions / SSO info are still in flight (or the
  // dashboard is being re-fetched after a sign-in); wait so the page doesn't flash the
  // wrong card (sign-in vs. SSO vs. wrong-organization).
  if (
    (isAccessDenied && isLoadingPermissions) ||
    (shouldShowSignIn && (isLoadingUserSSOInfo || isFetchingUserSSOInfo || isDashboardFetching))
  ) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Signed in, but the org's identity provider hasn't been satisfied — the only way
  // forward is the SSO flow, so show it instead of the OAuth buttons (ENG-8359). On
  // success the OIDC token is stored server-side, so re-fetching is all that's needed.
  if (ssoIdentityProvider) {
    return (
      <OidcLogin
        identityProvider={ssoIdentityProvider}
        onCancel={() => void handleSignOut()}
        onSuccess={() => {
          queryClient.invalidateQueries([QueryCacheKey.USER_SSO_INFO]);
          queryClient.invalidateQueries([QueryCacheKey.WEB_DASHBOARD, dashboardId]);
          queryClient.invalidateQueries([
            QueryCacheKey.WEB_DASHBOARD_PERMISSIONS,
            dashboardId,
            requestSuperAdminView,
          ]);
          queryClient.invalidateQueries([QueryCacheKey.CLIENT_FLAGS]);
        }}
      />
    );
  }

  const errorMessage = isNotFound
    ? "Dashboard not found"
    : isAccessDenied
    ? "This dashboard is only accessible to users from the same organization as the creator. Please sign in with an account from the correct organization."
    : dashboardError instanceof Error
    ? dashboardError.message
    : "Failed to load dashboard";

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        padding: "0 24px",
        backgroundColor: "#f9fafb",
      }}
    >
      {isAccessDenied ? (
        <div style={CARD_STYLE}>
          {isAccessDeniedBuildFailed ? (
            <>
              <CardIcon background="#fef3c7">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" stroke="#d97706" strokeWidth="2" />
                  <path d="M12 7v6" stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16.5" r="1.25" fill="#d97706" />
                </svg>
              </CardIcon>
              <h2 style={CARD_TITLE_STYLE}>Dashboard Unavailable</h2>
              <p style={CARD_BODY_STYLE}>
                This dashboard failed to build and can't be viewed. Please contact the dashboard
                owner.
              </p>
            </>
          ) : isAccessDeniedBuildInProgress ? (
            <>
              <CardIcon background="#dbeafe">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6.5 3.5h11M6.5 20.5h11M7.5 3.5c0 7 9 7 9 17M16.5 3.5c0 7-9 7-9 17"
                    stroke="#2563eb"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </CardIcon>
              <h2 style={CARD_TITLE_STYLE}>Dashboard Not Ready Yet</h2>
              <p style={CARD_BODY_STYLE}>
                This dashboard is still being built. Please try again in a few minutes.
              </p>
              <button onClick={() => window.location.reload()} style={SECONDARY_BUTTON_STYLE}>
                Try again
              </button>
            </>
          ) : !isAuthenticated ? (
            <>
              <CardIcon background="#dbeafe">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" fill="#2563eb" />
                  <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" fill="#2563eb" />
                </svg>
              </CardIcon>
              <h2 style={CARD_TITLE_STYLE}>Sign In Required</h2>
              <p style={CARD_BODY_STYLE}>
                Sign in with your organization account to view this dashboard.
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}
              >
                {showGoogleSignInFallback ? (
                  <button
                    onClick={handleFallbackGoogleLogin}
                    style={{ ...GIS_MATCHING_BUTTON_STYLE, justifyContent: "center" }}
                  >
                    Sign in with Google
                  </button>
                ) : (
                  <div style={{ height: 40 }} ref={googleSignInButtonRef}></div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: 215,
                  }}
                >
                  <div style={{ flex: 1, height: 1, backgroundColor: "#e5e7eb" }}></div>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af" }}>OR</span>
                  <div style={{ flex: 1, height: 1, backgroundColor: "#e5e7eb" }}></div>
                </div>
                {/* Custom button (msal works from any element, unlike GIS) styled to match
                    the GIS-rendered Google button above: 12px side padding, 18px icon with
                    8px gap, label centered in the remaining space. */}
                <button
                  onClick={() => void handleMicrosoftSignIn()}
                  style={{
                    ...GIS_MATCHING_BUTTON_STYLE,
                    ...(isMicrosoftSignInInFlight ? { opacity: 0.6, pointerEvents: "none" } : {}),
                  }}
                >
                  <MicrosoftLogo />
                  <span
                    style={{
                      flex: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Sign in with Microsoft
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <CardIcon background="#fef3c7">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="5" y="10" width="14" height="10" rx="2" fill="#d97706" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#d97706" strokeWidth="2" fill="none" />
                </svg>
              </CardIcon>
              <h2 style={CARD_TITLE_STYLE}>Access Restricted</h2>
              <p style={CARD_BODY_STYLE}>{errorMessage}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                  You're signed in as <strong>{userEmail}</strong>, but this dashboard belongs to a
                  different organization.
                </p>
                <div>
                  <button onClick={handleSignOut} style={SECONDARY_BUTTON_STYLE}>
                    Sign out and try a different account
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div
          style={{
            backgroundColor: "#fee2e2",
            border: "1px solid #ef4444",
            color: "#7f1d1d",
            padding: 16,
            borderRadius: 8,
          }}
        >
          <strong>Error:</strong> {errorMessage}
        </div>
      )}
    </div>
  );
};
